import type { ReactElement } from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { ComplianceStatusResponse } from '@fetmode/types';
import { ApiError } from '@fetmode/api-client';
import { useAuth } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import { SettingsStack } from './SettingsStack.js';

export function SettingsCompliancePage(): ReactElement {
  const { api } = useAuth();
  const [status, setStatus] = useState<ComplianceStatusResponse | null>(null);
  const [dateOfBirth, setDateOfBirth] = useState('2000-01-01');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadStatus = useCallback(async () => {
    const result = await api.getComplianceStatus();
    setStatus(result);
  }, [api]);

  useEffect(() => {
    void loadStatus()
      .catch(() => {
        setError(t('common.studio.compliance.loadError'));
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [loadStatus]);

  async function handleAttest(): Promise<void> {
    if (!acceptedTerms) {
      setError(t('common.studio.compliance.acceptTermsRequired'));
      return;
    }
    setIsSubmitting(true);
    setError(null);
    try {
      await api.submitAgeAttestation({
        dateOfBirth,
        acceptedTerms: true,
      });
      await loadStatus();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.compliance.attestError'));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleStartVerification(): Promise<void> {
    setIsSubmitting(true);
    setError(null);
    try {
      const session = await api.startAgeVerification();
      if (session.verificationUrl) {
        window.location.assign(session.verificationUrl);
        return;
      }
      await api.confirmAgeVerification();
      await loadStatus();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.studio.compliance.verifyError'));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <SettingsStack
      titleKey="common.studio.settings.complianceTitle"
      hintKey="common.studio.settings.complianceHint"
    >
      {isLoading ? (
        <p style={{ color: 'var(--fm-color-text-muted)' }}>{t('common.studio.compliance.loading')}</p>
      ) : null}
      {error ? <p className="fm-error">{error}</p> : null}

      {status ? (
        <div className="fm-card">
          <h2 style={{ marginTop: 0, fontSize: '1rem' }}>{t('common.studio.compliance.statusTitle')}</h2>
          <p style={{ margin: '0 0 0.75rem', fontSize: '0.875rem', color: 'var(--fm-color-text-muted)' }}>
            {t('common.studio.compliance.regionLine')
              .replace('{{region}}', status.jurisdictionCode)
              .replace('{{required}}', status.requiredTier)
              .replace('{{current}}', status.userTier)}
          </p>
          <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>
            {status.canAccess
              ? t('common.studio.dashboard.complianceReady')
              : t('common.studio.dashboard.complianceActionNeeded')}
          </p>

          {status.action === 'attest' ? (
            <>
              <label style={{ display: 'block', marginBottom: '0.75rem' }}>
                {t('common.studio.compliance.dateOfBirth')}
                <input
                  type="date"
                  className="fm-input"
                  value={dateOfBirth}
                  onChange={(event) => {
                    setDateOfBirth(event.target.value);
                  }}
                  style={{ display: 'block', width: '100%', marginTop: '0.25rem' }}
                />
              </label>
              <label style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(event) => {
                    setAcceptedTerms(event.target.checked);
                  }}
                />
                {t('common.studio.compliance.acceptTerms')}
              </label>
              <Button
                label={
                  isSubmitting
                    ? t('common.studio.compliance.submitting')
                    : t('common.studio.compliance.confirmAge')
                }
                disabled={!acceptedTerms || isSubmitting}
                onClick={() => {
                  void handleAttest();
                }}
              />
            </>
          ) : null}

          {status.action === 'verify' ? (
            <>
              <p style={{ margin: '0 0 1rem', fontSize: '0.875rem' }}>
                {t('common.studio.compliance.idRequired')}
              </p>
              <Button
                label={
                  isSubmitting
                    ? t('common.studio.compliance.starting')
                    : t('common.studio.compliance.startId')
                }
                disabled={isSubmitting}
                onClick={() => {
                  void handleStartVerification();
                }}
              />
              <p style={{ fontSize: '0.8125rem', color: 'var(--fm-color-text-muted)', marginTop: '0.75rem' }}>
                {t('common.studio.compliance.stubNote')}
              </p>
            </>
          ) : null}

          {status.action === 'denied' ? (
            <p className="fm-error" style={{ margin: 0 }}>
              {t('common.studio.compliance.denied')}
            </p>
          ) : null}
        </div>
      ) : null}
    </SettingsStack>
  );
}
